import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Import du logo Yros (place le fichier dans /assets/logo-yros.png ou adapte le chemin)
import logoYros from '../../assets/logo.png';
import LanguageSwitcher from '../../components/Header/LanguageSwitcher';

const COUNTRY_CODES = ['sn', 'ci', 'ne'];

const CountrySelect = () => {
  const { t } = useTranslation();
  const countries = COUNTRY_CODES.map(code => ({ value: code, label: t(`countries.${code}`) }));
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCode, setSelectedCode] = useState(() => {
    try { return localStorage.getItem('selectedCountryCode') || ''; } catch (e) { return ''; }
  });
  const [selectedLabel, setSelectedLabel] = useState(() => {
    try { return localStorage.getItem('selectedCountryLabel') || ''; } catch (e) { return ''; }
  });

  useEffect(() => {
    if (selectedCode && !selectedLabel) {
      setSelectedLabel(t(`countries.${selectedCode}`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCode, selectedLabel]);

  const handleSelect = (code) => {
    const label = t(`countries.${code}`);
    setSelectedCode(code);
    setSelectedLabel(label);
    try {
      localStorage.setItem('selectedCountryCode', code);
      localStorage.setItem('selectedCountryLabel', label);
      localStorage.setItem('selected_country', code);
    } catch (e) {}
  };

  const isSelected = Boolean(selectedCode);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50 p-6">
      <div className="max-w-xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8 relative">
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>

          {/* Logo Yros */}
          <div className="flex flex-col items-center mb-8">
            <img
              src={logoYros}
              alt="Logo Yros"
              className="w-20 h-20 mb-2 drop-shadow-lg"
              style={{ objectFit: 'contain' }}
            />
            <h1 className="text-3xl font-bold text-[--color-primary] mb-1">{t('countrySelect.welcomeTitle')}</h1>
            <p className="text-base text-gray-600">{t('countrySelect.welcomeSubtitle')}</p>
          </div>

          <div className="rounded-xl border p-8 bg-gradient-to-br from-orange-50 to-blue-50">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 text-center">{t('countrySelect.selectTitle')}</h3>
            <p className="text-sm text-gray-500 mb-6 text-center">{t('countrySelect.selectSubtitle')}</p>

            <div className="grid gap-4 grid-cols-1">
              {countries.map(c => {
                const active = selectedCode === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => handleSelect(c.value)}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-shadow focus:outline-none mb-2
                      ${active
                        ? 'bg-[--color-primary] text-white border-[--color-primary] shadow-lg scale-105'
                        : 'bg-white text-gray-800 border-gray-200 hover:shadow-md hover:border-orange-400'
                    }`}
                    aria-pressed={active}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-lg">{c.label}</span>
                    </div>
                    <div className="text-sm">
                      {active ? <span className="font-semibold">{t('countrySelect.chosen')}</span> : <span className="text-gray-400">{t('countrySelect.select')}</span>}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 text-sm text-gray-500 text-center">
              <strong>{t('countrySelect.importantLabel')} :</strong> {t('countrySelect.importantNote')}
            </div>
          </div>

          <div className="mt-8 flex gap-4">
            <button
              onClick={() => navigate('/login', { state: location.state })}
              className={`flex-1 py-3 rounded-xl font-semibold transition
                ${isSelected ? 'bg-[--color-primary] text-white hover:bg-[--color-orange]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
              disabled={!isSelected}
            >
              {t('countrySelect.loginButton')}
            </button>
          </div>

          {!isSelected && (
            <div className="mt-4 text-sm text-gray-500 text-center">
              {t('countrySelect.selectPrompt')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CountrySelect;
